import { useState, useEffect } from 'react';

import { Formik, Form, Field } from 'formik';

import * as Yup from 'yup';
import { TextField } from 'formik-mui';
import FormHelperText from '@mui/material/FormHelperText';
import { useRouter } from 'next/router';

import moment from 'moment';
import Phone from '@mui/icons-material/Phone';
import PropTypes from 'prop-types';
import getConfig from 'next/config';

import { FadeInAnimation, Button, Dropdown } from '../shared';

import { eventService } from '../../services/homii-services';

import { contactUsDomain } from '../../domain';

import useResponsive from '../../../utils/responsive';

const { publicRuntimeConfig } = getConfig();

const contactUsInitialValues = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    enquiryType: '',
    message: '',
    dates: moment().format('YYYY-MM-DD'),
    roomType: '',
    budget: '',
    city: '',
};

const contactUsValidationSchema = Yup.object({

    firstName: Yup.string().required('First name is required'),

    lastName: Yup.string().required('Last name is required'),

    email: Yup.string().email('Enter a valid email').required('Email is required'),

    phone: Yup.string()
        .matches(/^[0-9,+]*$/, 'Phone number is invalid')
        .min(9, 'Phone number is too short (minimum 9)')
        .max(15, 'Phone number is too long (maximum 15)')
        .required('Phone number is required'),

    enquiryType: Yup.string()
        .oneOf(['GENERAL_ENQUIRY', 'BOOK_A_VIEWING'])
        .required('Enquiry type is required'),

    message: Yup.string().when('enquiryType', {
        is: 'GENERAL_ENQUIRY',
        then: (schema) => schema.required('Message is required'),
        otherwise: (schema) => schema.notRequired(),
    }),

    roomType: Yup.string().when('enquiryType', {
        is: 'BOOK_A_VIEWING', 
        then: (schema) => schema
            .oneOf(
                [
                    'Bachelor Apartment',
                    'One Bedroom Apartment',
                    'Two Bedroom Apartment',
                    'Private Room',
                    'Shared Room',
                    'Daily',
                ],
                'Your stay type is required'
            )
            .required('Your stay type is required'),
        otherwise: (schema) => schema.notRequired(),
    }),

    budget: Yup.string().when('enquiryType', {
        is: 'BOOK_A_VIEWING',
        then: (schema) => schema
            .oneOf(
                ['1500 to 2500', '2501 to 4500', '4501 to 6500', '6501 to 8500', '8501 to 10000'],
                'Price range is required'
            )
            .required('Price range is required'),
        otherwise: (schema) => schema.notRequired(),
    }),

    city: Yup.string().when('enquiryType', {
        is: 'BOOK_A_VIEWING',
        then: (schema) => schema
            .oneOf(
                ['Durban', 'Cape Town', 'Pretoria', 'Johannesburg', 'Pietermaritzburg'],
                'City is required'
            )
            .required('City is required'),
        otherwise: (schema) => schema.notRequired(),
    }),

});

const enquiryTypes = [
    {
        label: 'General Enquiry',
        value: 'GENERAL_ENQUIRY',
        id: 'general-enquiry-select',
    },
    {
        label: 'Book a Viewing',
        value: 'BOOK_A_VIEWING',
        id: 'book-a-viewing',
    },
];

const roomTypes = [
    {
        label: 'Bachelor Apartment',
        value: 'Bachelor Apartment',
        id: 'bachelor-apartment-select',
    },
    {
        label: 'One Bedroom Apartment',
        value: 'One Bedroom Apartment',
        id: 'one-bedroom-apartment-select',
    },
    {
        label: 'Two Bedroom Apartment',
        value: 'Two Bedroom Apartment',
        id: 'two-bedroom-apartment-select',
    },
    {
        label: 'Private Room',
        value: 'Private Room',
        id: 'private-room-select',
    },
    {
        label: 'Shared Room',
        value: 'Shared Room',
        id: 'private-room-select',
    },
];

if (publicRuntimeConfig.HOMII_FEATURE_DAILY_TERM_ENABLED) {
    roomTypes.push({
        label: 'Daily',
        value: 'Daily',
        id: 'daily-select',
    });
}

const priceRanges = [
    {
        label: '1500 to 2500',
        value: '1500 to 2500',
        id: '1500-2500-select',
    },
    {
        label: '2501 to 4500',
        value: '2501 to 4500',
        id: '2501-4500-select',
    },
    {
        label: '4501 to 6500',
        value: '4501 to 6500',
        id: '4501-6500-select',
    },
    {
        label: '6501 to 8500',
        value: '6501 to 8500',
        id: '6501-8500-select',
    },
    {
        label: '8501 to 10000',
        value: '8501 to 10000',
        id: '8501-10000-select',
    },
];

const cities = [
    {
        label: 'Durban',
        value: 'Durban',
        id: 'durban-select',
    },
    {
        label: 'Cape Town',
        value: 'Cape Town',
        id: 'cape-town-select',
    },
    {
        label: 'Pretoria',
        value: 'Pretoria',
        id: 'pretoria-select',
    },
    {
        label: 'Johannesburg',
        value: 'Johannesburg',
        id: 'johannesburg-select',
    },
    {
        label: 'Pietermaritzburg',
        value: 'Pietermaritzburg',
        id: 'pietermaritzburg-select',
    },
];

const ConnectWithUs = () => {
    
    const [enquiryType, setEnquiryType] = useState(null);
    const [contactUsSubmitError, setContactUsSubmitError] = useState(null);
    const [successfullySubmitted, setSuccessfullySubmitted] = useState(false);
    const router = useRouter();
    const { isMobile } = useResponsive();

    useEffect(() => {
        if (successfullySubmitted) {
            eventService.logCustomEvent('succesful_contact_us_form_submission', {
                description:
                    'Thanks for getting in touch displayed after successful form submission',
            });
        }
    }, [successfullySubmitted]);

    const onSubmit = async ({
        firstName,
        lastName,
        email,
        phone,
        message,
        dates,
        roomType,
        budget,
        city,
    }) => {        
        try {
            if (enquiryType === 'GENERAL_ENQUIRY') {
                await contactUsDomain.submitGeneralInquiry(firstName, lastName, email, phone, message);
            }
            if (enquiryType === 'BOOK_A_VIEWING') {
                await contactUsDomain.submitBookViewing(
                    firstName,
                    lastName,
                    email,
                    phone,
                    dates,
                    roomType,
                    budget,
                    city,
                );
            }
            setContactUsSubmitError(null);
            setSuccessfullySubmitted(true);
            document.body.scrollTop = 0;
            document.documentElement.scrollTop = 0;
        } catch (error) {
            setContactUsSubmitError(error.message);
            setSuccessfullySubmitted(false);
        }
    };

    const onEnquiryChanged = (_enquiryType, event, formikOnChange) => {        
        setEnquiryType(_enquiryType);
        formikOnChange(event);
    };

    const onRoomTypeChanged = (roomType, event, formikOnChange) => {
        formikOnChange(event);
    };

    const onBudgetChanged = (budget, event, formikOnChange) => {
        formikOnChange(event);
    };

    const onCityChanged = (city, event, formikOnChange) => {
        formikOnChange(event);
    };

    const onClickGoHome = () => router.push('/');

    const ThankYouMessage = () => {
        if (!successfullySubmitted) {
            return null;
        }

        return (

            <FadeInAnimation>

                <div className='get-in-touch-form-thank-you-container get-in-touch-form-thank-you-height-50'>

                    <h3>Thanks for Getting in Touch!</h3>
                    <p>Someone from HOMii will be in touch ASAP</p>

                    <Button id="go-home-button" onClick={onClickGoHome}>
                        Back Home
                    </Button>

                </div>

            </FadeInAnimation>
        );
    };

    const ContactUsStepOne = ({ touched, errors, handleChange, handleBlur }) => {
        return (
            <>
                <FadeInAnimation>
                    <div>
                        <h5>1. Your Details</h5>
                    </div>
                </FadeInAnimation>

                <div className='get-in-touch-form-input-container'>

                    <Field className='get-in-touch-form-field-left'
                        component={TextField}
                        required
                        name="firstName"
                        id="firstName"
                        type="text"
                        label="Your Name"
                        variant="outlined"
                        color="secondary"
                        helperText={touched.firstName ? errors.firstName : ''}
                        error={touched.firstName && !!errors.firstName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        fullWidth
                    />

                    <Field className='get-in-touch-form-field-right'
                        component={TextField}
                        required
                        name="lastName"
                        id="lastName"
                        type="text"
                        label="Your Surname"
                        variant="outlined"
                        color="secondary"
                        helperText={touched.lastName ? errors.lastName : ''}
                        error={touched.lastName && !!errors.lastName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        fullWidth
                    />

                </div>

                <Field className='get-in-touch-form-field'
                    component={TextField}
                    required
                    name="email"
                    id="email"
                    type="email"
                    label="Email"
                    variant="outlined"
                    color="secondary"
                    helperText={touched.email ? errors.email : ''}
                    error={touched.email && !!errors.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    fullWidth
                />

                <Field className='get-in-touch-form-field'
                    component={TextField}
                    required
                    name="phone"
                    id="phone"
                    type="tel"
                    label="Phone"
                    variant="outlined"
                    color="secondary"
                    helperText={touched.phone ? errors.phone : ''}
                    error={touched.phone && !!errors.phone}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    fullWidth
                />
            </>
        );
    };

    const ContactUsStepTwo = ({ touched, errors, handleChange, handleBlur }) => {
        return (
            <>
                <FadeInAnimation>
                    <div>
                        <h5>2. How Can We Help You?</h5>
                    </div>
                </FadeInAnimation>

                <Dropdown
                    placeholder="Enquiry Type*"
                    selectId="enquiry_dropdown"
                    options={enquiryTypes}
                    onChange={(_enquiryType, event) => {
                        onEnquiryChanged(_enquiryType, event, handleChange);
                    }}
                    defaultValue={enquiryType}
                    name="enquiryType"
                    helperText={touched.enquiryType ? errors.enquiryType : ''}
                    error={touched.enquiryType && !!errors.enquiryType}
                    handleBlur={handleBlur}
                />

            </>
        );
    };

    const ContactUsStepThree = ({ touched, errors, handleChange, handleBlur }) => {
        switch (enquiryType) {
            case 'GENERAL_ENQUIRY':
                return (
                    <>
                        <FadeInAnimation>
                            <div>
                                <h5>3. Enter Your Message</h5>
                            </div>
                        </FadeInAnimation>

                        <Field className='get-in-touch-form-field'
                            component={TextField}
                            required
                            name="message"
                            id="message"
                            label="Your Message"
                            variant="outlined"
                            color="secondary"
                            multiline
                            rows={6}
                            helperText={touched.message ? errors.message : ''}
                            error={touched.message && !!errors.message}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            fullWidth
                        />

                    </>
                );
            case 'BOOK_A_VIEWING':
                return (
                    <>
                        <FadeInAnimation>
                            <div>
                                <h5>3. Book a Viewing</h5>
                            </div>
                        </FadeInAnimation>

                        <Field className='get-in-touch-form-field'
                            component={TextField}
                            required
                            name="dates"
                            id="date"
                            label="Viewing Date"
                            variant="outlined"
                            type="date"
                            color="secondary"
                            min={moment().format('YYYY-MM-DD')}
                            helperText={touched.dates ? errors.dates : ''}
                            error={touched.dates && !!errors.dates}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            fullWidth
                        />

                        <div className='connect-with-us-dropdown-container'>
                            <Dropdown
                                placeholder="Choose Your Stay*"
                                selectId="room-type-dropdown"
                                options={roomTypes}
                                onChange={(roomType, event) => {
                                    onRoomTypeChanged(roomType, event, handleChange);
                                }}
                                name="roomType"
                                helperText={touched.roomType ? errors.roomType : ''}
                                error={touched.roomType && !!errors.roomType}
                                handleBlur={handleBlur}
                            />
                        </div>

                        <div className='connect-with-us-dropdown-container'>
                            <Dropdown
                                placeholder="Price Range*"
                                selectId="budget-dropdown"
                                options={priceRanges}
                                onChange={(budget, event) => {
                                    onBudgetChanged(budget, event, handleChange);
                                }}
                                name="budget"
                                helperText={touched.budget ? errors.budget : ''}
                                error={touched.budget && !!errors.budget}
                                handleBlur={handleBlur}
                            />
                        </div>

                        <div className='connect-with-us-dropdown-container'>
                            <Dropdown
                                placeholder="City*"
                                selectId="city-dropdown"
                                options={cities}
                                onChange={(city, event) => {
                                    onCityChanged(city, event, handleChange);
                                }}
                                name="city"
                                helperText={touched.city ? errors.city : ''}
                                error={touched.city && !!errors.city}
                                handleBlur={handleBlur}
                            />
                        </div>

                    </>
                );
            default:
                return null;
        }
    };

    const onWhatsAppClick = () => {
        eventService.logCustomEvent('whatsapp_contact_us', {
            description: 'Clicked on whatsapp from Contact Us page',
        });
    };

    const onPhoneNumberClick = () => {
        eventService.logCustomEvent('click_to_call_contact_us', {
            description: 'Clicked on phone  from Contact Us page',
        });
    };

    const WhatsAppItem = () => {
        return (

            <div className='connect-with-us-contact-item-container'>

                <FadeInAnimation>

                    <div className='connect-with-us-contact-item' onClick={onWhatsAppClick}>
                        <a
                            id="open-whatsapp"
                            href="https://api.whatsapp.com/send?phone=27662818767"
                            target="_blank"
                            rel="noreferrer"
                        >
                            <div>
                                <img src="/assets/images/whatsapp_icon.png" alt="WhatsApp" />
                            </div>

                            <p>WhatsApp</p>
                        </a>
                    </div>

                    <div className='connect-with-us-contact-item' onClick={onPhoneNumberClick}>
                        <a id="open-dialer" href="tel:031 327 6400">

                            <div className='connect-with-us-contact-item-icon'>

                                <Phone style={{ color: '#fff' }} fontSize="small" />

                            </div>

                            <p className='connect-with-us-contact-number'>031 327 6400</p>

                        </a>
                    </div>

                </FadeInAnimation>

            </div>
        );

    };

    return (

        <div className='container-root container-root-maxWidthMd connect-with-us-container'>

            {!successfullySubmitted && (

                <div className='main-grid-container'>

                    <div className='main-grid-item main-grid-sm-6 main-grid-xs-12'>

                        <div className='connect-with-us-text-container'>

                            <FadeInAnimation>

                                <div>
                                    <h3>Contact Us</h3>
                                    <p className='connect-with-us-paragraph'>
                                        Need help ASAP? Give us a call, use the live chat, or check
                                        out the FAQ’s. Otherwise, use the form to get in touch.
                                    </p>
                                </div>

                            </FadeInAnimation>

                            {!isMobile && <WhatsAppItem />}

                        </div>

                    </div>

                    <div className='main-grid-item main-grid-sm-6 main-grid-xs-12'>

                        <div className='get-in-touch-small-form-container'>

                            <div>

                                <FadeInAnimation>
                                    <div>
                                        <h4>Get in Touch</h4>
                                    </div>
                                </FadeInAnimation>

                                <Formik
                                    initialValues={contactUsInitialValues}
                                    validationSchema={contactUsValidationSchema}
                                    onSubmit={onSubmit}
                                    validateOnBlur
                                    validateOnChange={false}
                                    id="connect-form"
                                >
                                    {({
                                        submitForm,
                                        isSubmitting,
                                        touched,
                                        errors,
                                        handleChange,
                                        handleBlur,
                                        handleReset,
                                        handleSubmit,
                                    }) => (
                                        
                                        <form onReset={handleReset} onSubmit={handleSubmit} id="connect-form">

                                            <ContactUsStepOne
                                                touched={touched}
                                                errors={errors}
                                                handleChange={handleChange}
                                                handleBlur={handleBlur}
                                            />

                                            <ContactUsStepTwo
                                                touched={touched}
                                                errors={errors}
                                                handleChange={handleChange}
                                                handleBlur={handleBlur}
                                            />

                                            <ContactUsStepThree
                                                touched={touched}
                                                errors={errors}
                                                handleChange={handleChange}
                                                handleBlur={handleBlur}
                                            />

                                            <FormHelperText error>
                                                {contactUsSubmitError}
                                            </FormHelperText>

                                            <FadeInAnimation>

                                                <Button
                                                    id="connect-with-us-button"
                                                    style={{ marginTop: '26px' }}
                                                    variant="contained"
                                                    color="primary"
                                                    isLoading={isSubmitting}
                                                    onClick={submitForm}
                                                >
                                                    Submit
                                                </Button>

                                            </FadeInAnimation>

                                        </form>
                                    )}

                                </Formik>

                            </div>

                        </div>

                    </div>

                </div>

            )}

            {isMobile && <WhatsAppItem />}

            <ThankYouMessage />

        </div>

    );
};

export default ConnectWithUs;

ConnectWithUs.propTypes = {
    touched: PropTypes.string,
    errors: PropTypes.string,

    handleChange: PropTypes.func,
    handleBlur: PropTypes.func,
};

